import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ChevronDown, ChevronRight, Copy, Edit2, EllipsisVerticalIcon, Monitor, Package, PackagePlus, Plus, RefreshCw, Trash2, UserPlus, X } from "lucide-react";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  PermissionGuard,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components";
import { useToast } from "@/components/ui/use-toast";
import { hasPartnerPortalAccess,useAuth, useConfirmDialog, useI18n } from "@/context";
import { usePermissions } from "@/context/permissions";
import { formatPrice } from "@/utils/monetary-formatter";

import * as clientsApi from "../clients/clients.api";
import { TClientExtendedResponse } from "../clients/clients.types";
import { ReplicateLpModal } from "../clients/sub-partners/replicate-lp-modal";
import { usePlataform } from "../platforms/platform.hooks";
import * as productsApi from "../products/products.api";
import { TProduct, TProductWithCapitalRange } from "../products/products.type";
import { isInfinitySyncedProduct } from "../products/products-sync-origin";
import { AssociateSubPartnersModal } from "./components/associate-sub-partners-modal";
import { useProjectPlans } from "./project-plans.hooks";
import * as projectsApi from "./projects.api";
import { TProjectDetail } from "./projects.types";

/** Entrada sintética no draft: produto inteiro sem faixas (id === product_id), alinhado a project-plans.hooks. */
function isWholeProductSyntheticRange(
  r: TProductWithCapitalRange,
  productId: string,
): boolean {
  return r.product_id === productId && r.id === productId;
}

/**
 * Produtos de assistência (e similares) têm capital_ranges registradas mas com
 * capital_value=0 e premium_value=0 — sem significado financeiro para seleção.
 * Nesses casos, tratamos o produto como "sem faixas" para fins do modal.
 */
function isEffectivelyRangeless(product: TProduct): boolean {
  if (!product.capital_ranges || product.capital_ranges.length === 0) return true;
  return product.capital_ranges.every(
    (r) => (Number(r.capital_value) || 0) === 0 && (Number(r.premium_value) || 0) === 0,
  );
}

function buildSyntheticWholeProductEntry(
  product: TProduct,
): TProductWithCapitalRange {
  return {
    id: product.id,
    product_id: product.id,
    name: product.name || "Sem nome",
    capital_value: 0,
    premium_value: Number(product.premium_value ?? 0),
    description: "",
    type: product.type || "",
    type_of_vigency: product.type_of_vigency,
    minimum_price: null,
    plan_name: null,
  };
}

export const ProjectDetailPage: React.FC = () => {
  const { clientId, projectId } = useParams<{ clientId: string; projectId: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { hasAnyPermission, hasPermission } = usePermissions();
  const { toast } = useToast();
  const { openDialog } = useConfirmDialog();
  const queryClient = useQueryClient();
  const [isProductsModalOpen, setIsProductsModalOpen] = React.useState(false);
  const [selectedCapitalRangesDraft, setSelectedCapitalRangesDraft] =
    React.useState<TProductWithCapitalRange[]>([]);
  const [expandedProductIds, setExpandedProductIds] = React.useState<string[]>(
    []
  );
  const [replicateModalOpen, setReplicateModalOpen] = React.useState(false);
  const [replicatePlatformId, setReplicatePlatformId] = React.useState("");
  const [associateSubPartnersOpen, setAssociateSubPartnersOpen] =
    React.useState(false);

  const { auth } = useAuth();
  const isPartnerPortalUser = hasPartnerPortalAccess(auth?.roles);

  // Planos: permissões internas SoulHub; LPs: permissões do portal parceiro
  const hasActionPermissions = hasAnyPermission(["partners:edit", "partners:delete"]);
  const hasPlatformActionPermissions = hasAnyPermission([
    "platform:update",
    "platform:delete",
  ]);

  // Buscar dados do cliente
  const { data: client } = useQuery<TClientExtendedResponse>({
    queryKey: ["client", clientId],
    queryFn: () => clientsApi.getClientById(clientId!),
    enabled: !!clientId,
    staleTime: 60_000,
  });

  // Buscar detalhes do projeto
  const {
    data: project,
    isLoading,
    error,
    refetch,
  } = useQuery<TProjectDetail>({
    queryKey: ["project", projectId],
    queryFn: () => projectsApi.getProjectById(projectId!),
    enabled: !!projectId,
    staleTime: 60_000,
    retry: 1, // Tentar apenas 1 vez antes de mostrar erro
  });

  const projectParticipantSubPartners = React.useMemo(
    () =>
      (project?.organization_participants ?? []).map((participant) => ({
        id: participant.id,
        name: participant.name,
      })),
    [project?.organization_participants],
  );

  const openReplicateModal = React.useCallback((platformId?: string) => {
    setReplicatePlatformId(platformId ?? "");
    setReplicateModalOpen(true);
  }, []);

  // Função para gerar e copiar link de cadastro
  const generateAndCopyLink = React.useCallback(
    (level: number, fatherOrganizationId: string | null) => {
      if (!projectId) return;

      const baseUrl = window.location.origin;
      const params = new URLSearchParams({
        projectId: projectId,
        level: level.toString(),
      });
      
      // Para nível 1, adicionar o organizationId do criador
      // Prioridade: organization_father do projeto > clientId (criador do projeto)
      if (level === 1) {
        const organizationId = project?.organization_father?.id || clientId || null;
        if (organizationId) {
          params.append("organizationId", organizationId);
        }
      }
      
      if (fatherOrganizationId) {
        params.append("fatherOrganizationId", fatherOrganizationId);
      }

      const link = `${baseUrl}/cadastro-organizacao?${params.toString()}`;
      
      navigator.clipboard.writeText(link).then(() => {
        toast({
          title: t("generic.success"),
          description: "Link copiado para a área de transferência!",
        });
      }).catch(() => {
        toast({
          title: t("generic.error"),
          description: "Erro ao copiar link",
          variant: "destructive",
        });
      });
    },
    [projectId, project, clientId, toast, t]
  );

  // Verificar se deve abrir o modal ao carregar a página
  // Nota: Este useEffect foi comentado pois as funções setSelectedLevel, 
  // setSelectedFatherOrganizationId e setIsRegisterModalOpen não estão mais definidas
  // Se necessário, essas funcionalidades devem ser reimplementadas
  // React.useEffect(() => {
  //   const state = location.state as any;
  //   if (state?.openRegisterModal && project) {
  //     const level = state.level || 1;
  //     setSelectedLevel(level);
  //     setSelectedFatherOrganizationId(project.organization_father?.id || null);
  //     setSelectedFatherOrganizationName(project.organization_father?.name || "");
  //     setIsRegisterModalOpen(true);
  //     // Limpar o state para não abrir novamente
  //     navigate(location.pathname, { replace: true, state: {} });
  //   }
  // }, [location.state, project, navigate, location.pathname]);

  // Hook para gerenciar planos do projeto
  const projectPlansHook = useProjectPlans(client || null, projectId);

  // Hook para gerenciar plataformas
  const plataformHook = usePlataform();
  const {
    platforms,
    isLoadingPlatforms,
    fetchPlatforms,
    deletePlatformCallback,
    isDeletingPlatform,
    bulkDeletePlatformsCallback,
    isBulkDeletingPlatforms,
  } = plataformHook;

  // Buscar plataformas do projeto
  React.useEffect(() => {
    if (client?.id && projectId) {
      fetchPlatforms(client.id, projectId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client?.id, projectId]);

  const handleDeletePlatform = React.useCallback(
    async (platformId: string) => {
      if (!client?.id) return;

      try {
        await deletePlatformCallback(client.id, platformId);
        // Recarregar plataformas após deletar
        if (projectId) {
          fetchPlatforms(client.id, projectId);
        }
      } catch (error) {
        console.error("Erro ao deletar plataforma:", error);
      }
    },
    [client?.id, projectId, deletePlatformCallback, fetchPlatforms]
  );

  const [selectedPlatformIds, setSelectedPlatformIds] = React.useState<
    string[]
  >([]);

  // Remove da seleção IDs que saíram da lista (ex: após exclusão bem-sucedida)
  React.useEffect(() => {
    setSelectedPlatformIds((prev) =>
      prev.filter((id) => platforms.some((platform) => platform.id === id))
    );
  }, [platforms]);

  const canBulkDeletePlatforms = hasPermission("platform:delete");
  const allPlatformsSelected =
    platforms.length > 0 && selectedPlatformIds.length === platforms.length;

  const togglePlatformSelectAll = (checked: boolean) => {
    setSelectedPlatformIds(checked ? platforms.map((platform) => platform.id) : []);
  };

  const togglePlatformSelectOne = (platformId: string, checked: boolean) => {
    setSelectedPlatformIds((prev) =>
      checked ? [...prev, platformId] : prev.filter((id) => id !== platformId)
    );
  };

  const handleBulkDeletePlatforms = React.useCallback(() => {
    if (!client?.id || selectedPlatformIds.length === 0) return;
    const count = selectedPlatformIds.length;
    openDialog(
      "Confirmar exclusão em massa",
      `Tem certeza que deseja excluir ${count} ${count === 1 ? "landing page" : "landing pages"}? Esta ação não pode ser desfeita.`,
      async () => {
        try {
          await bulkDeletePlatformsCallback(client.id, selectedPlatformIds);
          setSelectedPlatformIds([]);
          if (projectId) {
            fetchPlatforms(client.id, projectId);
          }
        } catch (error) {
          console.error("Erro ao excluir plataformas em massa:", error);
        }
      }
    );
  }, [
    client?.id,
    projectId,
    selectedPlatformIds,
    bulkDeletePlatformsCallback,
    fetchPlatforms,
    openDialog,
  ]);


  // Buscar produtos do projeto
  const {
    data: projectProducts = [],
    isLoading: isLoadingProjectProducts,
  } = useQuery<Array<{ id: string; name: string | null }>>({
    queryKey: ["project-products", projectId],
    queryFn: () => projectsApi.getProjectProducts(projectId!),
    enabled: !!projectId,
    staleTime: 60_000,
  });

  // Estado para armazenar os capital ranges selecionados
  // Tentar recuperar do localStorage ao carregar
  const [selectedCapitalRanges, setSelectedCapitalRanges] = React.useState<
    TProductWithCapitalRange[]
  >(() => {
    if (projectId) {
      try {
        const stored = localStorage.getItem(
          `project-${projectId}-selected-capital-ranges`
        );
        if (stored) {
          return JSON.parse(stored);
        }
      } catch (e) {
        // Ignorar erros de parsing
      }
    }
    return [];
  });

  // Buscar detalhes completos dos produtos do projeto
  const { data: allProducts = [] } = useQuery<TProduct[]>({
    queryKey: ["all-products"],
    queryFn: () => productsApi.getProducts(),
    enabled: !!projectId,
    staleTime: 60_000,
  });

  React.useEffect(() => {
    if (!projectId) {
      return;
    }

    localStorage.setItem(
      `project-${projectId}-selected-capital-ranges`,
      JSON.stringify(selectedCapitalRanges)
    );
  }, [projectId, selectedCapitalRanges]);

  // Mapear produtos do projeto com seus detalhes completos e apenas os capital ranges selecionados
  const projectProductsWithDetails = React.useMemo(() => {
    if (!allProducts.length || !projectProducts.length) {
      return projectProducts.map((p) => ({
        ...p,
        type: "",
        capital_ranges: [],
        infinity_sync_source_organizations: undefined as string[] | null | undefined,
        product_integration_ref: undefined as string | null | undefined,
        infinity_sync_as_source: undefined as boolean | null | undefined,
      }));
    }

    return projectProducts.map((projectProduct) => {
      const fullProduct = allProducts.find((p) => p.id === projectProduct.id);
      if (fullProduct) {
        // Filtrar apenas os capital ranges que foram selecionados
        const selectedRangesForProduct = selectedCapitalRanges.filter(
          (cr) => cr.product_id === projectProduct.id
        );
        
        // Se houver capital ranges selecionados, usar apenas esses
        // Caso contrário, mostrar todos os capital ranges do produto
        const capitalRangesToShow = selectedRangesForProduct.length > 0
          ? selectedRangesForProduct.map((cr) => {
              // Encontrar o capital range completo no produto
              const fullRange = fullProduct.capital_ranges?.find((r) => r.id === cr.id);
              return fullRange || {
                id: cr.id,
                capital_value: cr.capital_value,
                premium_value: cr.premium_value,
                description: cr.description,
                plan_name: cr.plan_name,
                minimum_price: cr.minimum_price,
              };
            })
          : fullProduct.capital_ranges || [];

        return {
          id: fullProduct.id,
          name: fullProduct.name || projectProduct.name || "Sem nome",
          type: fullProduct.type || "",
          capital_ranges: capitalRangesToShow,
          infinity_sync_source_organizations: fullProduct.infinity_sync_source_organizations,
          product_integration_ref: fullProduct.product_integration_ref,
          infinity_sync_as_source: fullProduct.infinity_sync_as_source,
        };
      }
      return {
        ...projectProduct,
        type: "",
        capital_ranges: [],
        infinity_sync_source_organizations: undefined as string[] | null | undefined,
        product_integration_ref: undefined as string | null | undefined,
        infinity_sync_as_source: undefined as boolean | null | undefined,
      };
    });
  }, [projectProducts, allProducts, selectedCapitalRanges]);


  // Mutation para deletar projeto
  const deleteProjectMutation = useMutation({
    mutationFn: async () => {
      if (!projectId) {
        throw new Error("projectId não encontrado");
      }
      return projectsApi.deleteProject(projectId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", clientId] });
      toast({
        title: t("generic.success"),
        description: "Projeto deletado com sucesso!",
      });
      navigate(`/clients/${clientId}/projects/list`);
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message || "Erro ao deletar projeto",
        variant: "destructive",
      });
    },
  });

  const updateProjectProductsMutation = useMutation({
    mutationFn: async (productIds: string[]) => {
      if (!projectId) {
        throw new Error("projectId não encontrado");
      }

      return projectsApi.updateProjectProducts(projectId, productIds);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-products", projectId] });
      toast({
        title: t("generic.success"),
        description: "Produtos do projeto atualizados com sucesso!",
      });
      setIsProductsModalOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message || "Erro ao atualizar produtos do projeto",
        variant: "destructive",
      });
    },
  });

  const handleDeleteProject = React.useCallback(() => {
    if (!project) return;
    const projectName = project.project_name || "este projeto";
    openDialog(
      t("generic.confirmDelete"),
      projectName,
      () => {
        deleteProjectMutation.mutate();
      }
    );
  }, [project, openDialog, t, deleteProjectMutation]);

  const handleOpenProductsModal = React.useCallback(() => {
    // Modal only shows products NOT yet associated — start with empty draft
    setSelectedCapitalRangesDraft([]);
    setExpandedProductIds([]);
    setIsProductsModalOpen(true);
  }, []);

  const handleRemoveProduct = React.useCallback((productId: string) => {
    if (!projectId) return;
    const remaining = projectProducts
      .map((p) => p.id)
      .filter((id) => id !== productId);
    // Clean up persisted capital ranges for removed product
    setSelectedCapitalRanges((prev) =>
      prev.filter((r) => r.product_id !== productId),
    );
    updateProjectProductsMutation.mutate(remaining);
  }, [projectId, projectProducts, updateProjectProductsMutation]);

  const handleToggleExpandProduct = React.useCallback((productId: string) => {
    setExpandedProductIds((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  }, []);

  const handleToggleCapitalRange = React.useCallback(
    (
      product: TProduct,
      range: TProduct["capital_ranges"][number]
    ) => {
      setSelectedCapitalRangesDraft((prev) => {
        const exists = prev.some((item) => item.id === range.id);
        if (exists) {
          return prev.filter((item) => item.id !== range.id);
        }

        const mappedRange: TProductWithCapitalRange = {
          id: range.id,
          product_id: product.id,
          name:
            ((range as { plan_name?: string | null }).plan_name as string) ||
            range.description ||
            product.name ||
            "Sem nome",
          capital_value: Number(range.capital_value || 0),
          premium_value: Number(range.premium_value || 0),
          description: range.description || "",
          type: product.type,
          type_of_vigency: product.type_of_vigency,
          minimum_price: range.minimum_price ?? null,
          plan_name:
            ((range as { plan_name?: string | null }).plan_name as string) ||
            null,
        };

        return [...prev, mappedRange];
      });

    },
    []
  );

  const handleToggleWholeProduct = React.useCallback((product: TProduct) => {
    setSelectedCapitalRangesDraft((prev) => {
      const has = prev.some((r) => isWholeProductSyntheticRange(r, product.id));
      if (has) {
        return prev.filter((r) => !isWholeProductSyntheticRange(r, product.id));
      }
      return [...prev, buildSyntheticWholeProductEntry(product)];
    });
  }, []);

  const handleSaveProjectProducts = React.useCallback(() => {
    const draft = selectedCapitalRangesDraft;

    const hasRealCapitalRangeForProduct = (
      product: TProduct,
      ranges: TProductWithCapitalRange[],
    ) =>
      ranges.some(
        (r) =>
          r.product_id === product.id &&
          (product.capital_ranges ?? []).some((cr) => cr.id === r.id),
      );

    // Only validate newly-selected products (those in draft)
    const invalidProducts = allProducts.filter((product) => {
      const inDraft = draft.some((r) => r.product_id === product.id);
      if (!inDraft) return false;
      if (isEffectivelyRangeless(product)) return false;
      return !hasRealCapitalRangeForProduct(product, draft);
    });

    if (invalidProducts.length > 0) {
      toast({
        title: t("generic.error"),
        description:
          "Selecione pelo menos um capital range para cada produto que possui opções.",
        variant: "destructive",
      });
      return;
    }

    const newProductIds = Array.from(new Set(draft.map((r) => r.product_id)));

    if (newProductIds.length === 0) {
      toast({
        title: t("generic.error"),
        description: "Selecione ao menos um produto para associar.",
        variant: "destructive",
      });
      return;
    }

    // Merge new selections with existing — never removes existing products here
    const existingIds = projectProducts.map((p) => p.id);
    const mergedIds = Array.from(new Set([...existingIds, ...newProductIds]));

    // Persist capital range selections (merge, don't overwrite)
    setSelectedCapitalRanges((prev) => {
      const withoutNew = prev.filter(
        (r) => !newProductIds.includes(r.product_id),
      );
      return [...withoutNew, ...draft];
    });

    updateProjectProductsMutation.mutate(mergedIds);
  }, [
    allProducts,
    selectedCapitalRangesDraft,
    projectProducts,
    t,
    toast,
    updateProjectProductsMutation,
  ]);

  const getTypeClientText = (type: "affinity" | "affiliate") => {
    switch (type) {
      case "affinity":
        return "Afinidade";
      case "affiliate":
        return "Afiliados";
      default:
        return type;
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col w-full h-full space-y-6 p-4">
        <Card>
          <CardContent className="flex items-center justify-center py-8">
            <p className="text-muted-foreground">Carregando detalhes do projeto...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || (!isLoading && !project)) {
    const errorMessage = error instanceof Error 
      ? error.message 
      : error 
        ? "Erro interno do servidor. Por favor, tente novamente mais tarde."
        : "Projeto não encontrado.";
    
    return (
      <div className="flex flex-col w-full h-full space-y-6 p-4">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-8 space-y-4">
            <div className="text-center space-y-2">
              <p className="text-destructive font-medium text-lg">
                Erro ao carregar projeto
              </p>
              <p className="text-sm text-muted-foreground max-w-md">
                {errorMessage}
              </p>
              {error && (error as any)?.status === 500 && (
                <p className="text-xs text-muted-foreground mt-2">
                  Se o problema persistir, entre em contato com o suporte técnico.
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => refetch()}
              >
                Tentar Novamente
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(`/clients/${clientId}/projects/list`)}
              >
                Voltar para Lista de Projetos
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Garantir que project existe antes de renderizar
  if (!project) {
    return null;
  }

  return (
    <div className="flex flex-col w-full h-full space-y-6 p-4 pb-12">
      {/* Header */}
      <div className="flex flex-row items-start justify-between">
        <div className="flex flex-col w-full px-2">
          <div className="flex items-center gap-3 mb-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate(`/clients/${clientId}/projects/list`)}
              size="sm"
              className="p-3 hover:bg-muted/50 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">
              {project.project_name || "Detalhes do Projeto"}
            </h1>
          </div>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            {client && (
              <span>
                Parceiro: <span className="font-medium text-foreground">{client.name}</span>
              </span>
            )}
            <Badge variant={project.type_client === "affinity" ? "default" : "secondary"}>
              {getTypeClientText(project.type_client)}
            </Badge>
            {project.type_client === "affiliate" && project.level_hierarchical && (
              <Badge variant="secondary">
                Níveis hierárquicos: {project.level_hierarchical}
              </Badge>
            )}
          </div>
        </div>
        {!isPartnerPortalUser && (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/clients/${clientId}/projects/${projectId}/edit`)}
              className="gap-2"
            >
              <Edit2 className="h-4 w-4" />
              Editar Projeto
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDeleteProject}
              disabled={deleteProjectMutation.isPending}
              className="gap-2"
            >
              <Trash2 className="h-4 w-4" />
              {deleteProjectMutation.isPending ? t("generic.deleting") : t("generic.delete")}
            </Button>
          </div>
        )}
      </div>

      {/* Detalhes do Projeto */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Usuários Comerciais */}
        {project.user_commercials && project.user_commercials.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <h3 className="text-lg font-semibold mb-4">Usuários Comerciais</h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead className="text-right">Comissão (%)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {project.user_commercials.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.name}</TableCell>
                      <TableCell className="text-right">
                        {(user.commission_percentage / 100).toFixed(2)}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Hunters */}
        {project.hunter && project.hunter.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <h3 className="text-lg font-semibold mb-4">Hunters</h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {project.hunter.map((hunter) => (
                    <TableRow key={hunter.id}>
                      <TableCell className="font-medium">{hunter.name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Partners - Agrupados por nível para projetos afiliados */}
        {(project.partner && project.partner.length > 0) || project.type_client === "affiliate" ? (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Parceiros</h3>
                <div className="flex items-center gap-2">
                  {project.type_client === "affiliate" && project.level_hierarchical && (
                    <Badge variant="secondary">
                      {project.level_hierarchical} {project.level_hierarchical === 1 ? "nível definido" : "níveis definidos"}
                    </Badge>
                  )}
                  {project.type_client === "affiliate" && (
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => {
                        // Se não houver parceiros, começar no nível 1 com a organização pai do projeto
                        let level = 1;
                        let fatherId: string | null = project.organization_father?.id || null;
                        
                        if (project.partner && project.partner.length > 0) {
                          // Encontrar o maior nível existente
                          const maxLevel = Math.max(...project.partner.map(p => p.level_hierarchy));
                          const nextLevel = maxLevel + 1;
                          // Se o próximo nível exceder o máximo definido, usar o último nível
                          level = project.level_hierarchical && nextLevel > project.level_hierarchical 
                            ? project.level_hierarchical 
                            : nextLevel;
                          
                          // Encontrar uma organização do nível anterior
                          const fatherPartner = project.partner.find(p => p.level_hierarchy === level - 1);
                          fatherId = fatherPartner?.id || project.organization_father?.id || null;
                        }
                        
                        generateAndCopyLink(level, fatherId);
                      }}
                      className="gap-2"
                    >
                      <Copy className="h-4 w-4" />
                      Gerar Link de Cadastro
                    </Button>
                  )}
                </div>
              </div>
              {project.partner && project.partner.length > 0 ? (
                project.type_client === "affiliate" ? (
                  // Para projetos afiliados: agrupar por nível
                  (() => {
                  const partnersByLevel = project.partner.reduce((acc, partner) => {
                    const level = partner.level_hierarchy;
                    if (!acc[level]) {
                      acc[level] = [];
                    }
                    acc[level].push(partner);
                    return acc;
                  }, {} as Record<number, typeof project.partner>);

                  const levels = Object.keys(partnersByLevel)
                    .map(Number)
                    .sort((a, b) => a - b);

                  return (
                    <div className="space-y-6">
                      {levels.map((level) => (
                        <div key={level} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-sm font-semibold">
                                Nível {level}
                              </Badge>
                              <span className="text-sm text-muted-foreground">
                                {partnersByLevel[level].length}{" "}
                                {partnersByLevel[level].length === 1 ? "organização" : "organizações"}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              {/* Botão para cadastrar mais organizações no mesmo nível */}
                              <Button
                                type="button"
                                size="sm"
                                variant="secondary"
                                onClick={() => {
                                  // Determinar o fatherOrganizationId correto
                                  let fatherOrgId: string | null = null;
                                  
                                  if (level === 1) {
                                    // Para nível 1, usar a organização pai do projeto
                                    fatherOrgId = project.organization_father?.id || null;
                                  } else {
                                    // Para níveis superiores, usar uma organização do nível anterior
                                    const previousLevel = level - 1;
                                    const previousLevelOrgs = partnersByLevel[previousLevel];
                                    if (previousLevelOrgs && previousLevelOrgs.length > 0) {
                                      fatherOrgId = previousLevelOrgs[0].id;
                                    }
                                  }
                                  
                                  generateAndCopyLink(level, fatherOrgId);
                                }}
                                className="gap-2"
                              >
                                <Copy className="h-4 w-4" />
                                Adicionar ao Nível {level}
                              </Button>
                              
                              {/* Botão para gerar link do próximo nível */}
                              {project.level_hierarchical && level < project.level_hierarchical && (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    // Encontrar uma organização deste nível para ser pai
                                    const fatherPartner = partnersByLevel[level][0];
                                    generateAndCopyLink(level + 1, fatherPartner?.id || null);
                                  }}
                                  className="gap-2"
                                >
                                  <Copy className="h-4 w-4" />
                                  Gerar Link Nível {level + 1}
                                </Button>
                              )}
                            </div>
                          </div>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Nome</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {partnersByLevel[level].map((partner) => (
                                <TableRow key={partner.id}>
                                  <TableCell className="font-medium">{partner.name}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      ))}
                    </div>
                  );
                })()
              ) : (
                // Para projetos de afinidade: tabela simples
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {project.partner.map((partner) => (
                      <TableRow key={partner.id}>
                        <TableCell className="font-medium">{partner.name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )
              ) : (
                <div className="flex flex-col items-center justify-center py-8">
                  <p className="text-muted-foreground mb-4">
                    Nenhuma organização cadastrada ainda.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        ) : null}
      </div>

      {/* Parceiros Participantes */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">
              Parceiros Participantes (
              {project.organization_participants?.length ?? 0})
            </h3>
            <Button
              type="button"
              variant="outline"
              className="gap-2"
              disabled={!clientId || !projectId}
              onClick={() => setAssociateSubPartnersOpen(true)}
            >
              <UserPlus className="h-4 w-4" />
              Associar sub-parceiro
            </Button>
          </div>
          {(project.organization_participants?.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nenhum sub-parceiro associado a este projeto.
            </p>
          ) : (
            <div className="max-h-56 overflow-y-auto rounded-md border">
              <Table>
                <TableHeader className="sticky top-0 bg-background z-10">
                  <TableRow>
                    <TableHead>Nome</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...(project.organization_participants ?? [])]
                    .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"))
                    .map((participant) => (
                      <TableRow key={participant.id}>
                        <TableCell className="font-medium">
                          {participant.name}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {clientId && projectId && (
        <AssociateSubPartnersModal
          open={associateSubPartnersOpen}
          onOpenChange={setAssociateSubPartnersOpen}
          clientId={clientId}
          projectId={projectId}
          alreadyLinkedIds={(project.organization_participants ?? []).map(
            (p) => p.id,
          )}
        />
      )}

      {/* Produtos do Projeto */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">
              Produtos ({projectProducts.length})
            </h3>
            {!isPartnerPortalUser && (
              <Button
                type="button"
                variant="outline"
                className="gap-2"
                onClick={handleOpenProductsModal}
              >
                <Package className="h-4 w-4" />
                Associar Produtos
              </Button>
            )}
          </div>
          {isLoadingProjectProducts ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Carregando produtos...</p>
              </div>
            </div>
          ) : projectProducts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Package className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">
                Nenhum produto associado a este projeto.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome do Produto</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Opções Disponíveis</TableHead>
                  {!isPartnerPortalUser && (
                    <TableHead className="w-20 text-right">Ações</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {projectProductsWithDetails.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell>
                      <div className="flex flex-col gap-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{product.name || "Sem nome"}</span>
                          {isInfinitySyncedProduct(product) && (
                            <Badge
                              variant="outline"
                              className="shrink-0 gap-1 text-xs font-normal border-primary/40 text-primary"
                            >
                              <RefreshCw className="h-3 w-3" />
                              {t("products.sync.badge")}
                            </Badge>
                          )}
                        </div>
                        {isInfinitySyncedProduct(product) &&
                          (product.infinity_sync_source_organizations?.length ?? 0) > 0 && (
                            <div className="flex flex-wrap gap-1 mt-0.5">
                              {product.infinity_sync_source_organizations!.map((label: string) => (
                                <span
                                  key={label}
                                  className="inline-flex max-w-full rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                                  title={label}
                                >
                                  {label}
                                </span>
                              ))}
                            </div>
                          )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {product.type ? t(`product.type.${product.type}`) : "-"}
                    </TableCell>
                    <TableCell>
                      {product.capital_ranges && product.capital_ranges.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {product.capital_ranges.map((range, index) => (
                            <Badge key={range.id || index} variant="secondary" className="text-xs">
                              {(range as any).plan_name || range.description || `Opção ${index + 1}`}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">Sem opções</span>
                      )}
                    </TableCell>
                    {!isPartnerPortalUser && (
                      <TableCell className="text-right">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          title="Remover produto"
                          disabled={updateProjectProductsMutation.isPending}
                          onClick={() =>
                            openDialog(
                              "Remover produto",
                              `Remover "${product.name}" deste projeto?`,
                              () => handleRemoveProduct(product.id),
                            )
                          }
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={isProductsModalOpen} onOpenChange={setIsProductsModalOpen}>
        <DialogContent className="!w-[80vw] !max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Associar Produtos ao Projeto</DialogTitle>
            <DialogDescription>
              Selecione os capital ranges dos produtos que têm faixas, ou associe
              produtos inteiros quando não houver faixas (ex.: crédito). Estes
              últimos usam jornada na landing para cotação — configure a jornada
              no cadastro do produto.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="text-sm text-muted-foreground">
              {new Set(selectedCapitalRangesDraft.map((r) => r.product_id)).size}{" "}
              produto(s) selecionado(s) para adicionar
            </div>

            <div className="border rounded-lg p-4 space-y-4">
              {(() => {
                const associatedIds = new Set(projectProducts.map((p) => p.id));
                const unassociated = allProducts.filter((p) => !associatedIds.has(p.id));
                if (unassociated.length === 0) {
                  return (
                    <p className="text-muted-foreground text-sm">
                      Todos os produtos disponíveis já estão associados a este projeto.
                    </p>
                  );
                }
                return unassociated.map((product) => {
                  const isExpanded = expandedProductIds.includes(product.id);
                  const wholeProductSelected = selectedCapitalRangesDraft.some(
                    (r) => isWholeProductSyntheticRange(r, product.id),
                  );
                  const realRangesSelectedCount =
                    selectedCapitalRangesDraft.filter(
                      (range) =>
                        range.product_id === product.id &&
                        !isWholeProductSyntheticRange(range, product.id),
                    ).length;
                  const effectivelyRangeless = isEffectivelyRangeless(product);
                  const rangeCount = effectivelyRangeless ? 0 : (product.capital_ranges?.length ?? 0);

                  return (
                    <div key={product.id} className="border rounded-md p-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <button
                          type="button"
                          className="flex items-center gap-3 text-left"
                          onClick={() => handleToggleExpandProduct(product.id)}
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          )}
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="font-medium">
                                {product.name || "Sem nome"}
                              </p>
                              {isInfinitySyncedProduct(product) && (
                                <Badge
                                  variant="outline"
                                  className="shrink-0 gap-1 text-xs font-normal border-primary/40 text-primary"
                                >
                                  <RefreshCw className="h-3 w-3" />
                                  {t("products.sync.badge")}
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {product.type
                                ? t(`product.type.${product.type}`)
                                : "Tipo não informado"}
                            </p>
                            {isInfinitySyncedProduct(product) &&
                              (product.infinity_sync_source_organizations?.length ?? 0) > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {product.infinity_sync_source_organizations!.map((label: string) => (
                                    <span
                                      key={label}
                                      className="inline-flex max-w-full rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                                      title={label}
                                    >
                                      {label}
                                    </span>
                                  ))}
                                </div>
                              )}
                          </div>
                        </button>
                        <p className="text-xs text-muted-foreground text-right max-w-[11rem]">
                          {rangeCount > 0
                            ? `${realRangesSelectedCount}/${rangeCount} faixa(s)`
                            : wholeProductSelected
                              ? "Associado · 0 faixas"
                              : "Não associado"}
                        </p>
                      </div>

                      {isExpanded && product.capital_ranges.length > 0 && !isEffectivelyRangeless(product) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pl-8">
                          {product.capital_ranges.map((range) => {
                            const checked = selectedCapitalRangesDraft.some(
                              (item) => item.id === range.id
                            );

                            return (
                              <label
                                key={range.id}
                                className="flex items-start gap-2 border rounded-md p-2 cursor-pointer"
                              >
                                <Checkbox
                                  checked={checked}
                                  onCheckedChange={() =>
                                    handleToggleCapitalRange(product, range)
                                  }
                                />
                                <div className="text-sm">
                                  <p className="font-medium">
                                    {(range as { plan_name?: string | null })
                                      .plan_name ||
                                      range.description ||
                                      "Faixa de capital"}
                                  </p>
                                  <p className="text-muted-foreground">
                                    Capital: {formatPrice(range.capital_value)}
                                  </p>
                                  <p className="text-muted-foreground">
                                    Prêmio: {formatPrice(range.premium_value)}
                                  </p>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      )}
                      {isExpanded && (product.capital_ranges.length === 0 || isEffectivelyRangeless(product)) && (
                        <div className="pl-8 space-y-2">
                          <label className="flex items-start gap-2 border rounded-md p-2 cursor-pointer">
                            <Checkbox
                              checked={wholeProductSelected}
                              onCheckedChange={() =>
                                handleToggleWholeProduct(product)
                              }
                            />
                            <div className="text-sm space-y-1">
                              <p className="font-medium">
                                Associar produto ao projeto
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Este tipo de produto usa jornada na landing para
                                cotação. Configure a jornada no cadastro do produto
                                (aba Jornada).
                              </p>
                            </div>
                          </label>
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsProductsModalOpen(false)}
              disabled={updateProjectProductsMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleSaveProjectProducts}
              disabled={updateProjectProductsMutation.isPending}
            >
              {updateProjectProductsMutation.isPending
                ? "Salvando..."
                : "Salvar associações"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Planos do Projeto */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">
              Planos ({project.plans?.length || 0})
            </h3>
            <Button
              type="button"
              onClick={() => {
                // Criar um produto para cada capital range selecionado
                const projectProductsFull: any[] = [];
                
                if (selectedCapitalRanges.length > 0) {
                  // Agrupar capital ranges por product_id
                  const rangesByProduct = selectedCapitalRanges.reduce((acc, cr) => {
                    if (!acc[cr.product_id]) {
                      acc[cr.product_id] = [];
                    }
                    acc[cr.product_id].push(cr);
                    return acc;
                  }, {} as Record<string, typeof selectedCapitalRanges>);
                  
                  // Para cada produto, criar uma entrada para cada capital range selecionado
                  Object.entries(rangesByProduct).forEach(([productId, ranges]) => {
                    const product = projectProductsWithDetails.find(p => p.id === productId);
                    if (product) {
                      ranges.forEach((cr) => {
                        projectProductsFull.push({
                          id: cr.id, // ID do capital range
                          product_id: productId,
                          name: cr.plan_name || cr.description || product.name || "",
                          type: product.type || "",
                          capital_value: cr.capital_value,
                          premium_value: cr.premium_value,
                          description: cr.description,
                          minimum_price: cr.minimum_price,
                          plan_name: cr.plan_name,
                        });
                      });
                    }
                  });
                } else {
                  // Se não houver capital ranges selecionados, usar os produtos principais
                  projectProductsFull.push(...projectProductsWithDetails.map(p => ({
                    id: p.id,
                    product_id: p.id,
                    name: p.name || "",
                    type: p.type || "",
                  })));
                }
                
                navigate(`/clients/${clientId}/plan-form`, {
                  state: {
                    selectedProducts: projectProductsFull,
                    selectedCapitalRanges: selectedCapitalRanges, // Passar os capital ranges selecionados
                    returnPath: `/clients/${clientId}/projects/${projectId}/create-plan`,
                    projectId: projectId,
                    projectType: project.type_client,
                    projectPartners: project.partner,
                    projectFather: client ? { id: client.id, name: client.name } : undefined,
                    projectParticipantOrganizations: project.organization_participants,
                    projectLevelHierarchical: project.level_hierarchical || undefined,
                    projectHunters: project.hunter || [],
                  },
                });
              }}
              className="gap-2"
            >
              <PackagePlus className="h-4 w-4" />
              Criar Plano
            </Button>
          </div>
          {!project.plans || project.plans.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8">
              <PackagePlus className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">
                Nenhum plano associado a este projeto.
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  // Criar um produto para cada capital range selecionado
                  const projectProductsFull: any[] = [];
                  
                  if (selectedCapitalRanges.length > 0) {
                    // Agrupar capital ranges por product_id
                    const rangesByProduct = selectedCapitalRanges.reduce((acc, cr) => {
                      if (!acc[cr.product_id]) {
                        acc[cr.product_id] = [];
                      }
                      acc[cr.product_id].push(cr);
                      return acc;
                    }, {} as Record<string, typeof selectedCapitalRanges>);
                    
                    // Para cada produto, criar uma entrada para cada capital range selecionado
                    Object.entries(rangesByProduct).forEach(([productId, ranges]) => {
                      const product = projectProductsWithDetails.find(p => p.id === productId);
                      if (product) {
                        ranges.forEach((cr) => {
                          projectProductsFull.push({
                            id: cr.id, // ID do capital range
                            product_id: productId,
                            name: cr.plan_name || cr.description || product.name || "",
                            type: product.type || "",
                            capital_value: cr.capital_value,
                            premium_value: cr.premium_value,
                            description: cr.description,
                            minimum_price: cr.minimum_price,
                            plan_name: cr.plan_name,
                          });
                        });
                      }
                    });
                  } else {
                    // Se não houver capital ranges selecionados, usar os produtos principais
                    projectProductsFull.push(...projectProductsWithDetails.map(p => ({
                      id: p.id,
                      product_id: p.id,
                      name: p.name || "",
                      type: p.type || "",
                    })));
                  }
                  
                  navigate(`/clients/${clientId}/plan-form`, {
                    state: {
                      selectedProducts: projectProductsFull,
                      selectedCapitalRanges: selectedCapitalRanges, // Passar os capital ranges selecionados
                      returnPath: `/clients/${clientId}/projects/${projectId}/create-plan`,
                      projectId: projectId,
                      projectType: project.type_client,
                      projectPartners: project.partner,
                      projectFather: client ? { id: client.id, name: client.name } : undefined,
                      projectParticipantOrganizations: project.organization_participants,
                      projectLevelHierarchical: project.level_hierarchical || undefined,
                      projectHunters: project.hunter || [],
                    },
                  });
                }}
              >
              Criar Primeiro Plano
              </Button>
            </div>
          ) : (
            <div className="max-h-72 overflow-y-auto rounded-md border">
              <Table>
                <TableHeader className="sticky top-0 bg-background z-10">
                  <TableRow>
                    <TableHead>Nome do Plano</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead className="text-right">Preço</TableHead>
                    {hasActionPermissions && (
                      <TableHead className="text-right">Ações</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {project.plans.map((plan) => (
                    <TableRow key={plan.id}>
                      <TableCell className="font-medium">{plan.name}</TableCell>
                      <TableCell>{plan.description || "-"}</TableCell>
                      <TableCell className="text-right">
                        {formatPrice(plan.plan_price)}
                      </TableCell>
                      {hasActionPermissions && (
                        <TableCell>
                          <div className="flex items-center justify-end">
                            <PermissionGuard
                              permissions={["partners:edit", "partners:delete"]}
                              requireAll={false}
                            >
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button size={"sm"} variant={"ghost"} className="p-2">
                                    <EllipsisVerticalIcon className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="p-auto" align="end">
                                  <PermissionGuard permission="partners:edit">
                                    <DropdownMenuItem
                                      className="w-full"
                                      onSelect={() => projectPlansHook.handleEditPlan(plan.id, {
                                        type_client: project.type_client,
                                        partner: project.partner,
                                        organization_participants: project.organization_participants,
                                        level_hierarchical: project.level_hierarchical,
                                        hunter: project.hunter,
                                        selectedCapitalRanges: selectedCapitalRanges,
                                      })}
                                    >
                                      <Edit2 className="h-4 w-4 mr-2" />
                                      {t("generic.edit")}
                                    </DropdownMenuItem>
                                  </PermissionGuard>
                                  <PermissionGuard permission="partners:delete">
                                    <DropdownMenuItem
                                      className="bg-red-400 w-full hover:bg-red-600"
                                      onSelect={() => projectPlansHook.handleDeletePlanWithConfirmation(plan.id, plan.name)}
                                      disabled={projectPlansHook.isDeletingPlan}
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      {projectPlansHook.isDeletingPlan ? t("generic.deleting") : t("generic.delete")}
                                    </DropdownMenuItem>
                                  </PermissionGuard>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </PermissionGuard>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Plataformas do Projeto */}
      <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                Landing Pages ({platforms.length})
              </h3>
              <div className="flex items-center gap-2">
                {canBulkDeletePlatforms && selectedPlatformIds.length > 0 && (
                  <>
                    <span className="text-sm text-muted-foreground">
                      {selectedPlatformIds.length}{" "}
                      {selectedPlatformIds.length === 1
                        ? "selecionada"
                        : "selecionadas"}
                    </span>
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={handleBulkDeletePlatforms}
                      disabled={isBulkDeletingPlatforms}
                      className="gap-2"
                    >
                      <Trash2 className="h-4 w-4" />
                      {isBulkDeletingPlatforms ? "Excluindo..." : "Excluir selecionadas"}
                    </Button>
                  </>
                )}
                <PermissionGuard
                  permissions={[
                    "platform:create",
                    "partners:edit",
                    "partner:self:view",
                  ]}
                  requireAll={false}
                >
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => openReplicateModal()}
                    disabled={
                      platforms.length === 0 ||
                      projectParticipantSubPartners.length === 0
                    }
                    className="gap-2"
                  >
                    <Copy className="h-4 w-4" />
                    Replicar LP
                  </Button>
                </PermissionGuard>
                <PermissionGuard permission="platform:create">
                  <Button
                    type="button"
                    onClick={() => navigate(`/clients/${clientId}/projects/${projectId}/platforms`, {
                      state: { createNew: true, returnPath: `/clients/${clientId}/projects/${projectId}` }
                    })}
                    className="gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Nova Landing Page
                  </Button>
                </PermissionGuard>
              </div>
            </div>
            {isLoadingPlatforms ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Carregando plataformas...</p>
                </div>
              </div>
            ) : platforms.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8">
                <Monitor className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">
                  Nenhuma landing page encontrada para este projeto.
                </p>
                <PermissionGuard permission="platform:create">
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/clients/${clientId}/projects/${projectId}/platforms`, {
                      state: { createNew: true, returnPath: `/clients/${clientId}/projects/${projectId}` }
                    })}
                    className="gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Criar Primeira Landing Page
                  </Button>
                </PermissionGuard>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    {canBulkDeletePlatforms && (
                      <TableHead className="w-10">
                        <Checkbox
                          checked={allPlatformsSelected}
                          onCheckedChange={(checked) =>
                            togglePlatformSelectAll(Boolean(checked))
                          }
                          aria-label="Selecionar todas as landing pages"
                        />
                      </TableHead>
                    )}
                    <TableHead>Título</TableHead>
                    <TableHead>Parceiro</TableHead>
                    <TableHead>Qtd de Planos</TableHead>
                    <TableHead>Data de Criação</TableHead>
                    <TableHead>Link</TableHead>
                    {hasPlatformActionPermissions && (
                      <TableHead>Ações</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {platforms.map((platform) => (
                    <TableRow key={platform.id}>
                      {canBulkDeletePlatforms && (
                        <TableCell>
                          <Checkbox
                            checked={selectedPlatformIds.includes(platform.id)}
                            onCheckedChange={(checked) =>
                              togglePlatformSelectOne(platform.id, Boolean(checked))
                            }
                            aria-label={`Selecionar landing page ${platform.landing_page?.title || ""}`}
                          />
                        </TableCell>
                      )}
                      <TableCell className="font-medium">
                        {platform.landing_page?.title}
                      </TableCell>
                      <TableCell>
                        {platform.organization_name || "—"}
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {platform.plans?.length || 0}{" "}
                          {(platform.plans?.length || 0) === 1 ? "plano" : "planos"}
                        </span>
                      </TableCell>
                      <TableCell>
                        {new Date(platform.created_at).toLocaleDateString("pt-BR")}
                      </TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          variant="link"
                          className="text-blue-600 underline hover:text-blue-800 transition-colors p-0 h-auto"
                          onClick={() => {
                            if (platform.landing_page?.lp_link) {
                              window.open(
                                platform.landing_page.lp_link,
                                "_blank",
                                "noopener,noreferrer"
                              );
                            }
                          }}
                        >
                          Abrir Landing Page
                        </Button>
                      </TableCell>
                      {hasPlatformActionPermissions && (
                        <TableCell>
                          <PermissionGuard
                            permissions={["platform:update", "platform:delete"]}
                            requireAll={false}
                          >
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size={"sm"} variant={"ghost"} className="p-2">
                                  <EllipsisVerticalIcon className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent className="p-auto" align="end">
                                <PermissionGuard
                                  permissions={[
                                    "platform:create",
                                    "partners:edit",
                                    "partner:self:view",
                                  ]}
                                  requireAll={false}
                                >
                                  {(!platform.organization_id ||
                                    platform.organization_id === clientId) &&
                                  projectParticipantSubPartners.length > 0 ? (
                                    <DropdownMenuItem
                                      className="w-full"
                                      onClick={() =>
                                        openReplicateModal(platform.id)
                                      }
                                    >
                                      <Copy className="h-4 w-4 mr-2" />
                                      Replicar para parceiros
                                    </DropdownMenuItem>
                                  ) : null}
                                </PermissionGuard>
                                <PermissionGuard permission="platform:update">
                                  <DropdownMenuItem
                                    className="w-full"
                                    onClick={() =>
                                      navigate(
                                        `/clients/${clientId}/projects/${projectId}/platforms`,
                                        {
                                          state: {
                                            openEditPlatform: platform,
                                            returnPath: `/clients/${clientId}/projects/${projectId}`,
                                          },
                                        }
                                      )
                                    }
                                  >
                                    <Edit2 className="h-4 w-4 mr-2" />
                                    {t("generic.edit")}
                                  </DropdownMenuItem>
                                </PermissionGuard>
                                <PermissionGuard permission="platform:delete">
                                  <DropdownMenuItem
                                    className="bg-red-400 w-full hover:bg-red-600"
                                    onClick={() => handleDeletePlatform(platform.id)}
                                    disabled={isDeletingPlatform}
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    {isDeletingPlatform ? "Excluindo..." : t("generic.delete")}
                                  </DropdownMenuItem>
                                </PermissionGuard>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </PermissionGuard>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
          )}
        </CardContent>
      </Card>

      {clientId && projectId ? (
        <ReplicateLpModal
          open={replicateModalOpen}
          onOpenChange={(open) => {
            setReplicateModalOpen(open);
            if (!open) {
              setReplicatePlatformId("");
            }
          }}
          parentOrganizationId={clientId}
          projectId={projectId}
          subPartners={projectParticipantSubPartners}
          sourcePlatforms={platforms}
          initialPlatformId={replicatePlatformId || undefined}
          onReplicated={() => {
            if (client?.id && projectId) {
              fetchPlatforms(client.id, projectId);
            }
          }}
        />
      ) : null}

    </div>
  );
};

